
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/ui/password-input";
import { Loader2 } from "lucide-react";
import { useFormValidation } from "@/hooks/use-form-validation";

type EmailLoginFormProps = {
  isLoading: boolean;
  onSubmit: (email: string, password: string) => Promise<void>;
  onError: (error: string) => void;
};

export const EmailLoginForm: React.FC<EmailLoginFormProps> = ({
  isLoading,
  onSubmit,
  // onError is accepted by the component API but not used internally
}) => {
  const { form, handleChange, validateForm } = useFormValidation(
    {
      email: '',
      password: '',
      remember: ''
    },
    {
      email: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
      password: { required: true }
    }
  );

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    await onSubmit(form.email.value, form.password.value);
  };


  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="example@mail.com"
          value={form.email.value}
          onChange={handleChange}
          className={form.email.error && form.email.touched ? "border-destructive" : ""}
          disabled={isLoading}
        />
        {form.email.error && form.email.touched && (
          <p className="text-sm text-destructive">{form.email.error}</p>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Пароль</Label>
          <Link 
            to="/reset" 
            className="text-sm text-primary hover:underline"
          >
            Забыли пароль?
          </Link>
        </div>
        <PasswordInput
          id="password"
          name="password"
          placeholder="••••••••"
          value={form.password.value}
          onChange={handleChange}
          error={!!(form.password.error && form.password.touched)}
          disabled={isLoading}
        />
        {form.password.error && form.password.touched && (
          <p className="text-sm text-destructive">{form.password.error}</p>
        )}
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox id="remember" name="remember" disabled={isLoading} />
        <Label htmlFor="remember" className="text-sm">Запомнить меня</Label>
      </div>
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Войти
      </Button>
    </form>
  );
};
